import React, { FunctionComponent, useReducer, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  IReducerAction,
  IAppSettings,
  QueryKeys,
  FEATURE_FLAG,
  getQuerySettings,
} from '@dw/models/shared';

type TAppSettingsContextActions = 'SET_APP_SETTINGS';

interface IAppSettingsContext {
  initialized: boolean;
  appSettings: IAppSettings;
  featureEnabled: (featureFlag: FEATURE_FLAG) => boolean;
}

const initialAppSettingsState: IAppSettings = {
  buildVersion: window.buildVersion,
  deepLinkBaseDomain: '',
  apiBaseUrl: '',
  blobBaseUrl: '',
  googleApiKey: '',
  featureFlags: {},
  mobileAppSchemes: {
    android: '',
    ios: '',
    iosAppId: ''
  },
};
const initialState: IAppSettingsContext = {
  initialized: false,
  featureEnabled: () => false,
  appSettings: { ...initialAppSettingsState },
};
export const AppSettingsContext =
  React.createContext<IAppSettingsContext>(initialState);

const AppSettingsContextReducer = (
  state: IAppSettingsContext,
  action: IReducerAction<TAppSettingsContextActions>
): IAppSettingsContext => {
  switch (action.type) {
    case 'SET_APP_SETTINGS':
      return {
        ...state,
        initialized: !!action.payload,
        appSettings: action.payload,
      };
    default:
      return state;
  }
};

interface IAppSettingsContextProviderProps {
  children: React.ReactNode;
}
export const AppSettingsContextProvider: FunctionComponent<
  IAppSettingsContextProviderProps
> = (props: IAppSettingsContextProviderProps) => {
  const [state, dispatch] = useReducer(AppSettingsContextReducer, {
    ...initialState,
  });

  const querySettings = useMemo(() => ({
    ...getQuerySettings(),
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    staleTime: 0
  }), []);

  const { data: appSettings } = useQuery<IAppSettings, Error>({
    queryKey: [QueryKeys.AppSettings],
    queryFn: async () => {
      const result = await fetch('/appsettings.json', {
        cache: 'no-store',
        method: 'GET',
      });
      return await result.json();
    },
    ...querySettings
  });

  const featureEnabled = (featureFlag: FEATURE_FLAG): boolean => {
    return (
      state.appSettings &&
      (state.appSettings.featureFlags[featureFlag] ?? false)
    );
  };

  useEffect(() => {
    if (!appSettings) {
      return;
    }

    dispatch({
      type: 'SET_APP_SETTINGS',
      payload: appSettings
    });

    if (appSettings) {
      console.debug(
        `Environment: ${appSettings.environment}, Version: ${appSettings.buildVersion}`
      );
    }
  }, [appSettings]);

  if (!state.initialized) {
    return null;
  }

  return (
    <AppSettingsContext.Provider
      value={{
        ...state,
        featureEnabled,
      }}
    >
      {props.children}
    </AppSettingsContext.Provider>
  );
};
